import { Pill, ShieldAlert, HeartPulse, Phone, Plus, Check, Activity, Footprints, Moon } from "lucide-react";

export function PhoneFrame({ children, className = "" }) {
  return (
    <div className={`relative mx-auto w-[260px] ${className}`}>
      <div className="relative rounded-[2.5rem] border border-border bg-card p-2.5 shadow-2xl ring-1 ring-black/5 dark:ring-white/5">
        <div className="absolute left-1/2 top-2.5 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-foreground/90" />
        <div className="h-[520px] overflow-hidden rounded-[2rem] bg-background">
          {children}
        </div>
      </div>
    </div>
  );
}

function Header({ title }) {
  return (
    <div className="flex items-center justify-between px-5 pb-3 pt-8">
      <div>
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Kinect Nova AI</p>
        <h4 className="font-heading text-lg font-bold text-foreground">{title}</h4>
      </div>
      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary to-accent" />
    </div>
  );
}

export function DashboardScreen() {
  return (
    <div className="flex h-full flex-col">
      <Header title="Good morning, Sam" />
      <div className="grid grid-cols-2 gap-2.5 px-4">
        <div className="rounded-2xl bg-primary p-3 text-primary-foreground">
          <HeartPulse className="h-5 w-5" strokeWidth={1.75} />
          <p className="mt-3 text-2xl font-bold leading-none">72</p>
          <p className="text-[10px] opacity-80">bpm · resting</p>
        </div>
        <div className="rounded-2xl bg-secondary p-3">
          <Footprints className="h-5 w-5 text-primary" strokeWidth={1.75} />
          <p className="mt-3 text-2xl font-bold leading-none text-foreground">8,240</p>
          <p className="text-[10px] text-muted-foreground">steps today</p>
        </div>
        <div className="rounded-2xl bg-secondary p-3">
          <Moon className="h-5 w-5 text-primary" strokeWidth={1.75} />
          <p className="mt-3 text-2xl font-bold leading-none text-foreground">7h 20m</p>
          <p className="text-[10px] text-muted-foreground">sleep</p>
        </div>
        <div className="rounded-2xl bg-secondary p-3">
          <Activity className="h-5 w-5 text-primary" strokeWidth={1.75} />
          <p className="mt-3 text-2xl font-bold leading-none text-foreground">96%</p>
          <p className="text-[10px] text-muted-foreground">SpO₂</p>
        </div>
      </div>
      <div className="mx-4 mt-3 rounded-2xl border border-border p-3">
        <p className="text-[11px] font-semibold text-foreground">Next medication</p>
        <div className="mt-2 flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/15 text-primary">
            <Pill className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-medium text-foreground">Aspirin · 100mg</p>
            <p className="text-[10px] text-muted-foreground">Today · 8:00 AM</p>
          </div>
          <span className="rounded-full bg-primary/15 px-2 py-1 text-[9px] font-semibold text-primary">Due</span>
        </div>
      </div>
    </div>
  );
}

export function MedicationScreen() {
  const meds = [
    { name: "Aspirin 100mg", time: "8:00 AM", done: true },
    { name: "Vitamin D", time: "9:00 AM", done: true },
    { name: "Metformin 500mg", time: "1:00 PM", done: false },
    { name: "Lisinopril 10mg", time: "8:00 PM", done: false },
  ];
  return (
    <div className="flex h-full flex-col">
      <Header title="Medications" />
      <div className="space-y-2.5 px-4">
        {meds.map((m) => (
          <div key={m.name} className="flex items-center gap-3 rounded-2xl border border-border p-3">
            <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${m.done ? "bg-primary text-primary-foreground" : "bg-primary/15 text-primary"}`}>
              {m.done ? <Check className="h-4 w-4" /> : <Pill className="h-4 w-4" />}
            </div>
            <div className="flex-1">
              <p className="text-xs font-semibold text-foreground">{m.name}</p>
              <p className="text-[10px] text-muted-foreground">{m.time}</p>
            </div>
            {m.done && <span className="text-[9px] font-semibold text-primary">Taken</span>}
          </div>
        ))}
      </div>
      <button className="mx-4 mt-auto mb-6 flex items-center justify-center gap-2 rounded-full bg-primary py-3 text-xs font-semibold text-primary-foreground">
        <Plus className="h-4 w-4" /> Add medication
      </button>
    </div>
  );
}

export function SosScreen() {
  return (
    <div className="flex h-full flex-col items-center justify-center bg-gradient-to-b from-destructive/10 to-background px-6 text-center">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-destructive">Emergency SOS</p>
      <div className="relative my-8 flex h-36 w-36 items-center justify-center">
        <span className="absolute inset-0 animate-pulse-ring rounded-full bg-destructive/40" />
        <span className="absolute inset-0 animate-pulse-ring rounded-full bg-destructive/30" style={{ animationDelay: "0.6s" }} />
        <div className="relative flex h-28 w-28 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow-xl">
          <ShieldAlert className="h-12 w-12" strokeWidth={1.75} />
        </div>
      </div>
      <p className="text-sm font-semibold text-foreground">Hold to alert your circle</p>
      <p className="mt-1 text-[11px] text-muted-foreground">Live location will be shared instantly with 4 trusted contacts</p>
      <div className="mt-6 flex -space-x-2">
        {["from-primary to-accent", "from-amber-400 to-orange-500", "from-sky-400 to-blue-500", "from-rose-400 to-pink-500"].map((g) => (
          <div key={g} className={`h-8 w-8 rounded-full border-2 border-background bg-gradient-to-br ${g}`} />
        ))}
      </div>
    </div>
  );
}

export function CallScreen() {
  return (
    <div className="flex h-full flex-col items-center bg-gradient-to-b from-primary/10 to-background px-6 pt-12 text-center">
      <div className="h-24 w-24 rounded-full bg-gradient-to-br from-primary to-accent shadow-lg" />
      <h4 className="mt-5 font-heading text-lg font-bold text-foreground">Mom</h4>
      <p className="text-[11px] text-muted-foreground">Video call · 04:21</p>
      <div className="mt-auto mb-10 flex items-center gap-5">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary text-foreground">
          <Phone className="h-5 w-5" />
        </div>
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive text-destructive-foreground">
          <Phone className="h-6 w-6 rotate-[135deg]" />
        </div>
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary text-foreground">
          <HeartPulse className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}
